import React, { useContext, useEffect } from 'react';
import { lazyWithReload } from '@/utils/lazyWithReload';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import {
  ACCOUNTING_PAGE_PATH,
  ACCOUNTING_REPORTS_DETAILS_PAGE_FULL_PATH,
  ACCOUNTING_REPORTS_PAGE_PATH,
  ADD_AGREEMENT_FROM_BUSINESS_TO_COMPANY_PATH,
  ADD_AGREEMENT_FROM_COMPANY_PATH,
  ADD_AGREEMENT_FROM_MEMBER_TO_COMPANY_PATH,
  ADD_AGREEMENT_PATH,
  ADD_BANK_PAGE_PATH,
  ADD_BUSISNESS_TRAVEL_PAGE_PATH,
  ADD_CARD_PAGE_PATH,
  ADD_CONTRACTOR_PAGE_PATH,
  ADD_EMPLOYEE_PAGE_PATH,
  ADD_INVOICES_TO_PROJECT_FULL_PATH,
  ADVANCED_SEARCH_PURCHASES_PAGE_PATH,
  ADVANCED_SEARCH_SALES_PAGE_PATH,
  AGREEMENTS_DETAILS_PAGE_FULL_PATH,
  AGREEMENTS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_CONNECTED_BANKS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_DOCUMENTS_FOR_TRANSACTIONS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_MISSING_BANK_STATEMENTS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_OTHER_DOCUMENTS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_PAGE_PATH,
  ANNUAL_REPORT_DETAILS_PAYMENTS_FOR_DOCUMENTS_PAGE_PATH,
  ANNUAL_REPORT_PAGE_PATH,
  AUTOMATION_SETTINGS_PAGE_PATH,
  BANK_DETAILS_PAGE_FULL_PATH,
  BANKS_PAGE_PATH,
  BILLING_SETTINGS_PAGE_PATH,
  BUSINESS_INFO_PAGE_PATH,
  COMPANION_PAGE_PATH,
  CONTRACTORS_DETAILS_PAGE_PATH,
  CONTRACTORS_EDIT_PAGE_PATH_ID,
  CONTRACTORS_PAGE_PATH,
  CUSTOMER_DETAILS_PAGE_FULL_PATH,
  CUSTOMERS_PAGE_PATH,
  DASHBOARD_WALKTHROUGH_PATH,
  EDIT_COMPANY_MEMBER_DETAILS_PAGE_FULL_PATH,
  EDIT_INVOICE_PAGE_FULL_PATH,
  EMPLOYEE_DETAILS_PAGE_QUOTE_PATH,
  EMPLOYEES_DETAILS_PAGE_PATH,
  EMPLOYEES_EDIT_PAGE_PATH_ID,
  EMPLOYEES_PAGE_PATH,
  HOME_PAGE_PATH,
  INVOICE_PARSING_DETAILS_PAGE_FULL_PATH,
  INVOICING_LIST_PAGE_PATH,
  INVOICING_PAGE_PATH,
  LOGIN_PAGE_PATH,
  MARKETPLACE_PAGE_PATH,
  MATCH_INTERNAL_TRANSACTIONS_PAGE_FULL_PATH,
  MATCH_TRANSACTION_PAGE_PATH,
  MATCHING_DOCUMENT_PAGE_FULL_PATH,
  MATCHING_INVOICE_PAGE_FULL_PATH,
  OTHER_SETTINGS_PAGE_PATH,
  PROFILE_SETTINGS_PAGE_PATH,
  PROJECTS_PAGE_PATH,
  PURCHASES_PAGE_PATH,
  REFERRAL_HISTORY_PAGE_PATH,
  REFERRAL_SETTINGS_PAGE_PATH,
  SALES_PAGE_PATH,
  SECURE_DOWNLOAD_DETAILS_FULL_PATH,
  SEE_PROJECT_DETAILS_FULL_PATH,
  SEE_TRAVEL_DETAILS_PAGE_FULL_PATH,
  SETTINGS_PAGE_PATH,
  STATEMENTS_PAGE_PATH,
  SUBSCRIPTION_CHANGE_PLAN_PAGE_FULL_PATH,
  SUBSCRIPTION_SETTINGS_PAGE_PATH,
  SUPPORT_PAGE_PATH,
  COMMUNICATION_CENTER_PAGE_PATH,
  SUPPORT_TICKET_PAGE_FULL_PATH,
  TEAM_MEMBER_SETTINGS_PAGE_FULL_PATH,
  TEAM_MEMBER_SETTINGS_PAGE_PATH,
  TEAM_SETTINGS_PAGE_PATH,
  TRANSACTIONS_PAGE_PATH,
  TRAVELS_PAGE_PATH,
  UPDATE_INVOICE_PAGE_FULL_PATH,
  UPDATE_PASSPORT,
  WORKFLOWS_PAGE_PATH,
  SALES_INVOICE_WORKFLOW_PAGE_PATH,
  PAYMENT_PAGE_PATH,
  MISSING_BANK_PAGE_FULL_PATH
} from '@/constants/routes';
import { AppLoadingScreen } from '@companio/companio_ui';
import { AuthContext } from './contexts/AuthContext';
import { useGetCurrentUser } from './hooks/queries/useGetCurrentUser';
import { CompanyType, UserRole } from './types/user';
import CrispManager from './CrispManager';
import { TourManager } from './components/Tour/TourManager';
import PaymentPage from './pages/Payment/Payment';

const AppLayout = lazyWithReload(() => import('./layouts/AppLayout/AppLayout'));
const SecureDownloadLayout = lazyWithReload(
  () => import('./layouts/SecureDownloadLayout/SecureDownloadLayout')
);
const LoginPage = lazyWithReload(() => import('./pages/Login/Login.page'));
const HomePage = lazyWithReload(() => import('@/pages/Home/Home.page'));
const SalesPage = lazyWithReload(() => import('./pages/Sales/Sales.page'));
const PurchasesPage = lazyWithReload(() => import('./pages/Purchases/Purchases.page'));
const AddInvoicePage = lazyWithReload(() => import('./pages/AddInvoice/AddInvoice.page'));
const InvoiceMatchingPage = lazyWithReload(
  () => import('./pages/InvoiceMatching/InvoiceMatching.page')
);
const BanksPage = lazyWithReload(() => import('./pages/Banks/Banks.page'));
const AddBankPage = lazyWithReload(() => import('@/pages/Banks/AddBank/AddBank.page'));
const BankDetailsPage = lazyWithReload(() => import('@/pages/BankDetails/BankDetails.page'));
const CustomersPage = lazyWithReload(() => import('@/pages/Customers/Customers.page'));
const CustomerDetailsPage = lazyWithReload(
  () => import('@/pages/Customers/CustomerDetails/CustomerDetails.page')
);
const UpdateInvoicePage = lazyWithReload(() => import('@/pages/UpdateInvoice/UpdateInvoice.page'));
const SettingsPage = lazyWithReload(() => import('@/pages/Settings/Settings.page'));
const NotFound404Page = lazyWithReload(() => import('@/pages/NotFound404/NotFound404.page'));
const ProfileSettingsPage = lazyWithReload(
  () => import('@/pages/ProfileSettings/ProfileSettings.page')
);
const TeamPage = lazyWithReload(() => import('@/pages/Team/Team.page'));
const AddOrEditUserPage = lazyWithReload(() => import('@/pages/AddOrEditUser/AddOrEditUser.page'));
const BillingPage = lazyWithReload(() => import('@/pages/Billing/Billing.page'));
const AutomationPage = lazyWithReload(() => import('@/pages/Automation/Automation.page'));
const AddCardPage = lazyWithReload(() => import('@/pages/AddCard/AddCard.page'));
const ReferralAndDiscountsPage = lazyWithReload(
  () => import('@/pages/ReferralAndDiscounts/ReferralAndDiscounts.page')
);
const ReferralHistoryPage = lazyWithReload(
  () => import('@/pages/ReferralHistory/ReferralHistoryPage')
);
const OtherSettingsPage = lazyWithReload(() => import('@/pages/OtherSettings/OtherSettings.page'));
const AnnualReportPage = lazyWithReload(() => import('@/pages/AnnualReport/AnnualReport.page'));
const ContractorsPage = lazyWithReload(() => import('@/pages/Contractors/Contractors.page'));
const AddContractorPage = lazyWithReload(
  () => import('./pages/Contractors/components/AddContractor/AddContractor.page')
);
const ContractorsDetailsPage = lazyWithReload(
  () => import('@/pages/ContractorsDetails/ContractorsDetails.page')
);
const TravelsPage = lazyWithReload(() => import('@/pages/Travels/Travels.page'));
const TravelDetailsPage = lazyWithReload(
  () => import('@/pages/Travels/TravelDetails/TravelDetails.page')
);
const AddTravelPage = lazyWithReload(() => import('@/pages/Travels/AddTravel/AddTravel.page'));
const EmployeesPage = lazyWithReload(() => import('@/pages/Employees/Employees.page'));
const AddEmployeePage = lazyWithReload(
  () => import('./pages/Employees/components/AddEmployee/AddEmployee.page')
);
const EmployeesDetailsPage = lazyWithReload(
  () => import('@/pages/EmployeesDetails/EmployeeDetails.page')
);
const EmployeeQuoteReviewPage = lazyWithReload(
  () => import('@/pages/EmployeeQuoteReview/EmployeeQuoteReview.page')
);
const EmployeeEditPagePage = lazyWithReload(
  () => import('@/pages/EmployeesDetails/components/EditEmployee/EditEmployee.page')
);
const ManageSubscriptionPage = lazyWithReload(
  () => import('@/pages/ManageSubscription/ManageSubscription.page')
);
const ChangeSubscriptionPage = lazyWithReload(
  () => import('@/pages/ChangeSubscription/ChangeSubscription.page')
);
const MatchDocumentPage = lazyWithReload(() => import('@/pages/MatchDocument/MatchDocument.page'));
const DashboardWalkthrough = lazyWithReload(
  () => import('@/pages/DashboardWalkthrough/DashboardWalkthrough.page')
);
const SupportPage = lazyWithReload(() => import('@/pages/Support/Support.page'));
const CommunicationCenterPage = lazyWithReload(
  () => import('@/pages/CommunicationCenter/CommunicationCenter.page')
);
const TicketDetailsPage = lazyWithReload(() => import('@/pages/Support/Ticket/TicketDetails.page'));
const AgreementsPage = lazyWithReload(() => import('@/pages/Agreements/Agreements.page'));
const AgreementDetailsPage = lazyWithReload(
  () => import('@/pages/AgreementDetails/AgreementDetails.page')
);
const AddAgreementsPage = lazyWithReload(() => import('@/pages/AddAgreement/AddAgreement.page'));
const AddAgreementBorrowFromCompanyPage = lazyWithReload(
  () => import('@/pages/AddAgreementBorrowFromCompany/AddAgreementBorrowFromCompany.page')
);
const AddAgreementBusinessLendToCompanyPage = lazyWithReload(
  () => import('@/pages/AddAgreementBusinessLendToCompany/AddAgreementBusinessLendToCompany.page')
);
const AddAgreementMemberLendToCompanyPage = lazyWithReload(
  () => import('@/pages/AddAgreementMemberLendToCompany/AddAgreementMemberLendToCompany.page')
);
const ProjectsPage = lazyWithReload(() => import('@/pages/Projects/Projects.page'));
const ProjectDetailsPage = lazyWithReload(() => import('@/pages/Projects/ProjectDetails.page'));
const AddInvoiceToProjectPage = lazyWithReload(() => import('@/pages/Projects/AddInvoices.page'));
const InvoiceParsingDetailsPage = lazyWithReload(
  () => import('@/pages/InvoiceParsingDetails/InvoiceParsingDetails.page')
);
const EditInvoicePage = lazyWithReload(() => import('@/pages/EditInvoice/EditInvoice.page'));
const MatchTransactionPage = lazyWithReload(
  () => import('@/pages/MatchTransaction/MatchTransaction.page')
);
const TransactionPage = lazyWithReload(() => import('@/pages/Transactions/Transactions.page'));
const InvoicingListPage = lazyWithReload(() => import('@/pages/Invoicing/InvoicingList.page'));
const StatementsPage = lazyWithReload(() => import('@/pages/Statements/Statements.page'));
const BusinessInfoPage = lazyWithReload(() => import('@/pages/BusinessInfo/BusinessInfo.page'));
const MatchInternalTransactionsPage = lazyWithReload(
  () => import('@/pages/MatchInternalTransactions/MatchInternalTransactions.page')
);
const CompanionPage = lazyWithReload(() => import('@/pages/Companion/Companion.page'));
const MarketplacePage = lazyWithReload(() => import('@/pages/Marketplace/Marketplace.page'));
const AccountingReportsPage = lazyWithReload(
  () => import('@/pages/AccountingReports/AccountingReports.page')
);
const AccountingReportsDetailsPage = lazyWithReload(
  () => import('@/pages/AccountingReportsDetails/AccountingReportsDetails.page')
);
const WorkflowsPage = lazyWithReload(() => import('@/pages/Workflows/Workflows.page'));
const SalesInvoiceWorkflowPage = lazyWithReload(
  () => import('@/pages/SalesInvoiceWorkflow/SalesInvoiceWorkflow.page')
);
const EditMemberDetailsPage = lazyWithReload(
  () => import('@/pages/EditMemberDetails/EditMemberDetails.page')
);
const AdvancedSearchSalesPage = lazyWithReload(
  () => import('@/pages/Sales/AdvancedSearchSales.page')
);
const AdvancedSearchPurchasesPage = lazyWithReload(
  () => import('@/pages/Purchases/AdvancedSearchPurchases.page')
);
const NewAnnualReportDetailsPage = lazyWithReload(
  () => import('@/pages/AnnualReportDetailsNew/AnnualReportDetails.page')
);
const NewAnnualReportDetailsConnectedBanksPage = lazyWithReload(
  () =>
    import(
      '@/pages/AnnualReportDetailsNew/AnnualReportDetailsConnectedBanks/AnnualReportDetailsConnectedBanks.page'
    )
);
const NewAnnualReportDetailsBankStatementsPage = lazyWithReload(
  () =>
    import(
      '@/pages/AnnualReportDetailsNew/AnnualReportDetailsBankStatements/AnnualReportDetailsBankStatements.page'
    )
);
const AnnualReportDetailsDocumentsForTransactionsPage = lazyWithReload(
  () =>
    import(
      '@/pages/AnnualReportDetailsNew/AnnualReportDetailsDocumentsForTransactions/AnnualReportDetailsDocumentsForTransactions.page'
    )
);
const AnnualReportDetailsPaymentsForDocumentsPage = lazyWithReload(
  () =>
    import(
      '@/pages/AnnualReportDetailsNew/AnnualReportDetailsPaymentsForDocuments/AnnualReportDetailsPaymentsForDocuments.page'
    )
);
const AnnualReportDetailsOtherDocumentsPage = lazyWithReload(
  () =>
    import(
      '@/pages/AnnualReportDetailsNew/AnnualReportDetailsOtherDocuments/AnnualReportDetailsOtherDocuments.page'
    )
);
const SecureDownloadPage = lazyWithReload(
  () => import('./pages/SecureDownload/SecureDownload.page')
);
const AccountingPage = lazyWithReload(() => import('./pages/Accounting/Accounting.page'));
const EditContractorPage = lazyWithReload(
  () => import('./pages/ContractorsDetails/components/EditContractor/EditContractor.page')
);
const UpdatePassportPage = lazyWithReload(
  () => import('./pages/Passport/components/UpdatePassport.page')
);
const MissingBankPage = React.lazy(() => import('./pages/MissingBank/MissingBank.page'));

function GuestRoutes() {
  const { isAuthenticated } = useContext(AuthContext);

  return isAuthenticated ? <Navigate to={{ pathname: HOME_PAGE_PATH }} /> : <Outlet />;
}

function ProtectedRoutes() {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  let redirectUrl = searchParams.get('redirect_url') || location.pathname + location.search;

  if (redirectUrl.startsWith('/')) {
    redirectUrl = redirectUrl.slice(1);
  }

  if (!isAuthenticated) {
    let redirectParam = '';

    if (redirectUrl) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('redirect_url', redirectUrl);
      redirectParam = `?${newSearchParams.toString()}`;
    }

    return <Navigate to={`${LOGIN_PAGE_PATH}${redirectParam}`} />;
  }

  return <Outlet />;
}

function RepresentativeRoutes() {
  const currentUserQuery = useGetCurrentUser();
  const userRole = currentUserQuery.data?.user.role;

  if (userRole !== UserRole.REPRESENTATIVE) {
    return <NotFound404Page />;
  }

  return <Outlet />;
}

function RoleProtectedRoutes({ allowedRoles }: { allowedRoles: UserRole[] }) {
  const currentUserQuery = useGetCurrentUser();
  const userRole = currentUserQuery.data?.user.role;

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <NotFound404Page />;
  }

  return <Outlet />;
}

function PlanProtectedRoutes({ plans }: { plans: CompanyType[] }) {
  const currentUserQuery = useGetCurrentUser();
  const companyPlan = currentUserQuery.data?.user.company.company_type;

  if (!plans.includes(companyPlan as CompanyType)) {
    return <NotFound404Page />;
  }

  return (
    <>
      <TourManager />
      <Outlet />
    </>
  );
}

export function Router() {
  const { isAuthenticated } = useContext(AuthContext);

  // this hides the crisp chat bubble on all pages
  useEffect(() => {
    if (!window.$crisp) window.$crisp = [];
    window.$crisp.push(['do', 'chat:hide']);
  }, []);

  if (isAuthenticated === null) {
    return <AppLoadingScreen />;
  }

  return (
    <>
      <CrispManager />

      <Routes>
        <Route element={<GuestRoutes />}>
          <Route
            path={LOGIN_PAGE_PATH}
            element={
              isAuthenticated ? <Navigate to={{ pathname: HOME_PAGE_PATH }} /> : <LoginPage />
            }
          />
        </Route>

        <Route element={<ProtectedRoutes />}>
          <Route element={<SecureDownloadLayout />}>
            <Route path={SECURE_DOWNLOAD_DETAILS_FULL_PATH} element={<SecureDownloadPage />} />
          </Route>
          <Route element={<AppLayout />}>
            {/* VIRTUAL PLAN ROUTES */}
            <Route index path={HOME_PAGE_PATH} element={<HomePage />} />
            <Route index path={DASHBOARD_WALKTHROUGH_PATH} element={<DashboardWalkthrough />} />
            <Route path={BUSINESS_INFO_PAGE_PATH} element={<BusinessInfoPage />} />
            <Route
              path={EDIT_COMPANY_MEMBER_DETAILS_PAGE_FULL_PATH}
              element={<EditMemberDetailsPage />}
            />
            <Route path={SETTINGS_PAGE_PATH} element={<SettingsPage />}>
              <Route index element={<Navigate to={{ pathname: PROFILE_SETTINGS_PAGE_PATH }} />} />
              <Route path={PROFILE_SETTINGS_PAGE_PATH} element={<ProfileSettingsPage />} />
              <Route path={TEAM_SETTINGS_PAGE_PATH} element={<TeamPage />} />
              <Route path={REFERRAL_SETTINGS_PAGE_PATH} element={<ReferralAndDiscountsPage />} />
              <Route path={BILLING_SETTINGS_PAGE_PATH} element={<BillingPage />} />
              <Route path={OTHER_SETTINGS_PAGE_PATH} element={<OtherSettingsPage />} />
              <Route element={<RepresentativeRoutes />}>
                <Route
                  path={SUBSCRIPTION_SETTINGS_PAGE_PATH}
                  element={<ManageSubscriptionPage />}
                />
              </Route>
            </Route>
            <Route path={ADD_CARD_PAGE_PATH} element={<AddCardPage />} />
            <Route path={REFERRAL_HISTORY_PAGE_PATH} element={<ReferralHistoryPage />} />
            <Route element={<RepresentativeRoutes />}>
              <Route
                path={SUBSCRIPTION_CHANGE_PLAN_PAGE_FULL_PATH}
                element={<ChangeSubscriptionPage />}
              />
            </Route>
            <Route path={SUPPORT_PAGE_PATH} element={<SupportPage />} />
            <Route path={COMMUNICATION_CENTER_PAGE_PATH} element={<CommunicationCenterPage />} />
            <Route path={SUPPORT_TICKET_PAGE_FULL_PATH} element={<TicketDetailsPage />} />
            <Route path={MARKETPLACE_PAGE_PATH} element={<MarketplacePage />} />
            <Route path={UPDATE_PASSPORT} element={<UpdatePassportPage />} />

            {/* PRO/PREMIUM/ULTRA PLAN ROUTES */}
            <Route
              element={
                <PlanProtectedRoutes
                  plans={[CompanyType.PRO, CompanyType.PREMIUM, CompanyType.ULTRA]}
                />
              }>
              <Route path={SETTINGS_PAGE_PATH} element={<SettingsPage />}>
                <Route path={AUTOMATION_SETTINGS_PAGE_PATH} element={<AutomationPage />} />
              </Route>
              <Route path={ADD_BANK_PAGE_PATH} element={<AddBankPage />} />
              <Route path={BANK_DETAILS_PAGE_FULL_PATH} element={<BankDetailsPage />} />
              <Route path={STATEMENTS_PAGE_PATH} element={<StatementsPage />} />
              <Route path={TRANSACTIONS_PAGE_PATH} element={<TransactionPage />} />

              <Route path={INVOICING_PAGE_PATH} element={<AddInvoicePage />} />
              <Route path={UPDATE_INVOICE_PAGE_FULL_PATH} element={<UpdateInvoicePage />} />
              <Route path={SALES_PAGE_PATH} element={<SalesPage />} />
              <Route path={PURCHASES_PAGE_PATH} element={<PurchasesPage />} />
              <Route path={MATCHING_INVOICE_PAGE_FULL_PATH} element={<InvoiceMatchingPage />} />
              <Route path={CUSTOMERS_PAGE_PATH} element={<CustomersPage />} />
              <Route path={CUSTOMER_DETAILS_PAGE_FULL_PATH} element={<CustomerDetailsPage />} />
              <Route path={TEAM_MEMBER_SETTINGS_PAGE_FULL_PATH} element={<AddOrEditUserPage />} />
              <Route path={TEAM_MEMBER_SETTINGS_PAGE_PATH} element={<AddOrEditUserPage />} />
              <Route path={TRAVELS_PAGE_PATH} element={<TravelsPage />} />
              <Route path={SEE_TRAVEL_DETAILS_PAGE_FULL_PATH} element={<TravelDetailsPage />} />
              <Route path={ADD_BUSISNESS_TRAVEL_PAGE_PATH} element={<AddTravelPage />} />
              <Route path={MATCHING_DOCUMENT_PAGE_FULL_PATH} element={<MatchDocumentPage />} />
              <Route path={PROJECTS_PAGE_PATH} element={<ProjectsPage />} />
              <Route path={SEE_PROJECT_DETAILS_FULL_PATH} element={<ProjectDetailsPage />} />
              <Route path={COMPANION_PAGE_PATH} element={<CompanionPage />} />
              <Route
                element={
                  <RoleProtectedRoutes allowedRoles={[UserRole.ADMIN, UserRole.REPRESENTATIVE]} />
                }>
                <Route path={CONTRACTORS_PAGE_PATH} element={<ContractorsPage />} />
                <Route path={ADD_CONTRACTOR_PAGE_PATH} element={<AddContractorPage />} />
                <Route path={CONTRACTORS_DETAILS_PAGE_PATH} element={<ContractorsDetailsPage />} />
                <Route path={CONTRACTORS_EDIT_PAGE_PATH_ID} element={<EditContractorPage />} />
                <Route path={EMPLOYEES_PAGE_PATH} element={<EmployeesPage />} />
                <Route path={ADD_EMPLOYEE_PAGE_PATH} element={<AddEmployeePage />} />
                <Route path={EMPLOYEES_DETAILS_PAGE_PATH} element={<EmployeesDetailsPage />} />
                <Route
                  path={EMPLOYEE_DETAILS_PAGE_QUOTE_PATH}
                  element={<EmployeeQuoteReviewPage />}
                />
                <Route path={EMPLOYEES_EDIT_PAGE_PATH_ID} element={<EmployeeEditPagePage />} />
                <Route path={AGREEMENTS_PAGE_PATH} element={<AgreementsPage />} />
                <Route
                  path={AGREEMENTS_DETAILS_PAGE_FULL_PATH}
                  element={<AgreementDetailsPage />}
                />
                <Route path={ADD_AGREEMENT_PATH} element={<AddAgreementsPage />} />
                <Route
                  path={ADD_AGREEMENT_FROM_BUSINESS_TO_COMPANY_PATH}
                  element={<AddAgreementBusinessLendToCompanyPage />}
                />
                <Route
                  path={ADD_AGREEMENT_FROM_COMPANY_PATH}
                  element={<AddAgreementBorrowFromCompanyPage />}
                />
                <Route
                  path={ADD_AGREEMENT_FROM_MEMBER_TO_COMPANY_PATH}
                  element={<AddAgreementMemberLendToCompanyPage />}
                />
              </Route>
              <Route
                path={INVOICE_PARSING_DETAILS_PAGE_FULL_PATH}
                element={<InvoiceParsingDetailsPage />}
              />
              <Route path={EDIT_INVOICE_PAGE_FULL_PATH} element={<EditInvoicePage />} />
              <Route path={MATCH_TRANSACTION_PAGE_PATH} element={<MatchTransactionPage />} />
              <Route path={INVOICING_LIST_PAGE_PATH} element={<InvoicingListPage />} />
              <Route
                path={MATCH_INTERNAL_TRANSACTIONS_PAGE_FULL_PATH}
                element={<MatchInternalTransactionsPage />}
              />
              <Route path={ACCOUNTING_REPORTS_PAGE_PATH} element={<AccountingReportsPage />} />
              <Route
                path={ACCOUNTING_REPORTS_DETAILS_PAGE_FULL_PATH}
                element={<AccountingReportsDetailsPage />}
              />
              <Route path={ACCOUNTING_PAGE_PATH} element={<AccountingPage />} />
              <Route path={SEE_PROJECT_DETAILS_FULL_PATH} element={<ProjectDetailsPage />} />
              <Route
                path={ADD_INVOICES_TO_PROJECT_FULL_PATH}
                element={<AddInvoiceToProjectPage />}
              />
              <Route path={WORKFLOWS_PAGE_PATH} element={<WorkflowsPage />} />
              <Route
                path={SALES_INVOICE_WORKFLOW_PAGE_PATH}
                element={<SalesInvoiceWorkflowPage />}
              />
              <Route path={ADVANCED_SEARCH_SALES_PAGE_PATH} element={<AdvancedSearchSalesPage />} />
              <Route
                path={ADVANCED_SEARCH_PURCHASES_PAGE_PATH}
                element={<AdvancedSearchPurchasesPage />}
              />
              <Route path={UPDATE_PASSPORT} element={<UpdatePassportPage />} />
            </Route>
            <Route
              element={
                <PlanProtectedRoutes
                  plans={[
                    CompanyType.PRO,
                    CompanyType.PREMIUM,
                    CompanyType.ULTRA,
                    CompanyType.VIRTUAL
                  ]}
                />
              }>
              <Route path={ANNUAL_REPORT_PAGE_PATH} element={<AnnualReportPage />} />
              <Route
                path={ANNUAL_REPORT_DETAILS_PAGE_PATH}
                element={<NewAnnualReportDetailsPage />}
              />
              <Route
                path={ANNUAL_REPORT_DETAILS_CONNECTED_BANKS_PAGE_PATH}
                element={<NewAnnualReportDetailsConnectedBanksPage />}
              />
              <Route
                path={ANNUAL_REPORT_DETAILS_MISSING_BANK_STATEMENTS_PAGE_PATH}
                element={<NewAnnualReportDetailsBankStatementsPage />}
              />
              <Route
                path={ANNUAL_REPORT_DETAILS_DOCUMENTS_FOR_TRANSACTIONS_PAGE_PATH}
                element={<AnnualReportDetailsDocumentsForTransactionsPage />}
              />
              <Route
                path={ANNUAL_REPORT_DETAILS_PAYMENTS_FOR_DOCUMENTS_PAGE_PATH}
                element={<AnnualReportDetailsPaymentsForDocumentsPage />}
              />
              <Route
                path={ANNUAL_REPORT_DETAILS_OTHER_DOCUMENTS_PAGE_PATH}
                element={<AnnualReportDetailsOtherDocumentsPage />}
              />
              <Route path={BANKS_PAGE_PATH} element={<BanksPage />} />
              <Route path={MISSING_BANK_PAGE_FULL_PATH} element={<MissingBankPage />} />
            </Route>

            <Route path={PAYMENT_PAGE_PATH} element={<PaymentPage />} />

            <Route path="*" element={<NotFound404Page />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
